import { useLocation } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import { isStageEligible, matchesRoute } from "@/lib/guidance/disclosure";
import { TOUR_LIST } from "@/lib/guidance/tours.config";
import type { TTourId } from "@/lib/guidance/types";
import { useGuidanceStage } from "@/hooks/useGuidanceStage";
import { useStore } from "@/store/store";

import { TourController } from "./TourController";

const AUTO_START_DELAY_MS = 600;

export function GuidanceProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const profile = useStore((state) => state.profile);
  const hydrateGuidance = useStore((state) => state.hydrateGuidance);
  const activeTourId = useStore((state) => state.activeTourId);
  const setActiveTourId = useStore((state) => state.setActiveTourId);
  const isTourFinished = useStore((state) => state.isTourFinished);
  const isLoadingData = useStore((state) => state.isLoading);
  const { stage, isGuidanceHydrated } = useGuidanceStage();

  useEffect(() => {
    if (profile?.id) {
      void hydrateGuidance(profile.id);
    }
  }, [hydrateGuidance, profile?.id]);

  const autoStartTourId = useMemo((): TTourId | null => {
    if (!isGuidanceHydrated || activeTourId || isLoadingData) {
      return null;
    }

    const pathname = location.pathname;

    for (const tour of TOUR_LIST) {
      if (tour.autoStart !== "firstVisit") {
        continue;
      }
      if (isTourFinished(tour.id)) {
        continue;
      }
      if (!isStageEligible(stage, tour.requiredStage)) {
        continue;
      }
      if (!matchesRoute(pathname, tour.routes)) {
        continue;
      }
      return tour.id;
    }

    return null;
  }, [activeTourId, isGuidanceHydrated, isLoadingData, isTourFinished, location.pathname, stage]);

  useEffect(() => {
    if (!autoStartTourId) {
      return;
    }

    const timer = window.setTimeout(() => {
      setActiveTourId(autoStartTourId);
    }, AUTO_START_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [autoStartTourId, setActiveTourId]);

  return (
    <>
      {children}
      {activeTourId ? <TourController tourId={activeTourId} /> : null}
    </>
  );
}
